import React, { useEffect, useState, useRef, lazy, memo } from 'react';
import { CheckIcon, XIcon, ShieldCheckIcon, MapPinIcon, ChevronDownIcon, ClockIcon, StarIcon, UsersIcon, LayersIcon, FileTextIcon, ZapIcon } from 'lucide-react';
// Miniaturas optimizadas (webp via auto=format, ancho reducido y baja calidad)
// para que la carga sea liviana y rapida en movil.
const IMG = (id: string) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=160&h=160&q=45`;
// Nombres y ciudades de Argentina para el popup de compra verificada.
const BUYER_NAMES = ['Martina G.', 'Federico R.', 'Valentina P.', 'Santiago M.', 'Lucia F.', 'Tomas A.', 'Camila S.', 'Joaquin D.', 'Sofia L.', 'Mateo B.', 'Julieta C.', 'Nicolas V.', 'Agustina R.', 'Bruno T.', 'Florencia N.'];
const BUYER_CITIES = ['Cordoba', 'Buenos Aires', 'La Plata', 'Rosario', 'Mendoza', 'CABA', 'Mar del Plata', 'Tucuman', 'Salta', 'Neuquen', 'Santa Fe', 'Bahia Blanca', 'San Juan', 'Parana'];
const BUYER_OFFERS = ['Kit ARQ Total\u2122', 'Kit ARQ Core\u2122'];
const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];
type Purchase = {
  id: number;
  name: string;
  city: string;
  offer: string;
  minsAgo: number;
};
export function LandingPage() {
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 46,
    s: 0
  });
  const [isStickyVisible, setIsStickyVisible] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openTomo, setOpenTomo] = useState<number | null>(null);
  const [openBono, setOpenBono] = useState<number | null>(null);
  const [selectedPains, setSelectedPains] = useState<number[]>([]);
  const togglePain = (index: number) => {
    setSelectedPains(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };
  useEffect(() => {
    const total = 23 * 3600 + 46 * 60;
    const saved = localStorage.getItem('arq_timer_end');
    let end: number;
    if (saved) {
      end = parseInt(saved);
    } else {
      end = Date.now() + total * 1000;
      localStorage.setItem('arq_timer_end', end.toString());
    }
    const tick = () => {
      const rem = Math.max(0, Math.floor((end - Date.now()) / 1000));
      const h = Math.floor(rem / 3600);
      const m = Math.floor(rem % 3600 / 60);
      const s = rem % 60;
      setTimeLeft({
        h,
        m,
        s
      });
      if (rem > 0) {
        setTimeout(tick, 1000);
      }
    };
    tick();
  }, []);
  useEffect(() => {
    const hero = document.getElementById('hero-section');
    if (!hero) return;
    const observer = new IntersectionObserver(([e]) => {
      setIsStickyVisible(!e.isIntersecting);
    }, {
      threshold: 0
    });
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);
  // Popup de compra verificada: aparece cada 40-60s, se autocierra a los 6s.
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const purchaseIdRef = useRef(0);
  useEffect(() => {
    let hideTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    const show = () => {
      purchaseIdRef.current += 1;
      setPurchase({
        id: purchaseIdRef.current,
        name: pick(BUYER_NAMES),
        city: pick(BUYER_CITIES),
        offer: pick(BUYER_OFFERS),
        minsAgo: Math.floor(Math.random() * 9) + 1
      });
      hideTimer = setTimeout(() => setPurchase(null), 6000);
      // Reprograma entre 40 y 60 segundos.
      nextTimer = setTimeout(show, (40 + Math.random() * 20) * 1000);
    };
    // Primer aviso a los ~8s de cargar la pagina.
    nextTimer = setTimeout(show, 8000);
    return () => {
      clearTimeout(hideTimer);
      clearTimeout(nextTimer);
    };
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const [selectedPlan, setSelectedPlan] = useState<'core' | 'total'>('total');
  const CHECKOUT_URLS = {
    core: 'https://checkout.infodigitalcore.shop/cart/50098816647407:1',
    total: 'https://checkout.infodigitalcore.shop/cart/50098819039471:1'
  };
  const PLAN_PRICE = {
    core: '$19.990',
    total: '$29.990'
  };
  const PLAN_NAME = {
    core: 'Kit ARQ Core™',
    total: 'Kit ARQ Total™'
  };
  const goToCheckout = () => {
    window.location.href = CHECKOUT_URLS[selectedPlan];
  };
  const PlanSelector = ({
    variant = 'light'
  }: {
    variant?: 'light' | 'dark';
  }) => {
    const dark = variant === 'dark';
    const plans = [{
      id: 'total' as const,
      name: 'Kit ARQ Total™',
      oldPrice: '$43.990',
      price: '$29.990',
      save: 'Ahorrás $14.000',
      desc: '8 tomos + 6 bonos + 2 tomos exclusivos: Hormigón Armado y Ejercicio Profesional.',
      featured: true
    }, {
      id: 'core' as const,
      name: 'Kit ARQ Core™',
      oldPrice: '$34.990',
      price: '$19.990',
      save: 'Ahorrás $15.000',
      desc: '6 tomos base + los 6 bonos digitales completos.',
      featured: false
    }];
    return <div className="max-w-[720px] mx-auto" data-id="element-2">
        {/* Temporizador de fin de oferta */}
        <div className={`flex items-center justify-center gap-2 sm:gap-3 rounded-xl py-2.5 px-4 mb-4 border ${dark ? 'bg-white/[0.06] border-gold/25' : 'bg-danger/[0.06] border-danger/25'}`} data-id="element-3">
          <span className={`flex items-center gap-1.5 font-display text-[11px] sm:text-xs font-bold tracking-[1px] uppercase ${dark ? 'text-gold' : 'text-danger'}`} data-id="element-4">
            <ClockIcon className="w-4 h-4" data-id="element-5" />
            La oferta termina en
          </span>
          <span className={`font-display text-lg sm:text-xl font-extrabold tabular-nums tracking-[1px] ${dark ? 'text-white' : 'text-navy'}`} data-id="element-6">
            {String(timeLeft.h).padStart(2, '0')}:
            {String(timeLeft.m).padStart(2, '0')}:
            {String(timeLeft.s).padStart(2, '0')}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 items-stretch" data-id="element-7">
          {plans.map(plan => {
          const active = selectedPlan === plan.id;
          return <button key={plan.id} type="button" onClick={() => setSelectedPlan(plan.id)} aria-pressed={active} className={`relative text-left rounded-[14px] border-2 p-4 sm:p-5 pt-5 transition-all duration-200 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold ${active ? 'border-gold bg-gold/[0.07] shadow-[0_8px_30px_rgba(201,168,76,0.2)]' : dark ? 'border-white/15 bg-white/[0.04] hover:border-white/30' : 'border-gray-200 bg-white hover:border-gray-400'}`} data-id="element-8">
                {plan.featured && <span className="absolute -top-2.5 left-4 bg-gold text-navy-deep font-display text-[10px] font-extrabold tracking-[1.5px] uppercase py-1 px-2.5 rounded" data-id="element-9">
                    ★ Más elegido
                  </span>}
                <div className="flex items-start justify-between gap-3 mb-1" data-id="element-10">
                  <span className={`font-display text-[16px] sm:text-[17px] font-extrabold uppercase leading-tight ${dark ? 'text-white' : 'text-navy'}`} data-id="element-11">
                    {plan.name}
                  </span>
                  <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors duration-200 ${active ? 'border-gold bg-gold' : dark ? 'border-white/30' : 'border-gray-300'}`} data-id="element-12">
                    {active && <CheckIcon className="w-3 h-3 text-navy-deep" strokeWidth={3} data-id="element-13" />}
                  </span>
                </div>
                <div className="flex items-baseline gap-2 mb-1.5" data-id="element-14">
                  <span className={`font-display text-sm font-semibold line-through ${dark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-15">
                    {plan.oldPrice}
                  </span>
                  <span className={`font-display text-[30px] font-extrabold leading-none ${dark ? 'text-white' : 'text-navy'}`} data-id="element-16">
                    {plan.price}
                  </span>
                </div>
                <span className="inline-block bg-success/15 text-success text-[11px] font-semibold py-[2px] px-2 rounded mb-2" data-id="element-17">
                  {plan.save}
                </span>
                <p className={`text-[12px] leading-[1.45] ${dark ? 'text-white/55' : 'text-text-light'}`} data-id="element-18">
                  {plan.desc}
                </p>
              </button>;
        })}
        </div>
        <button onClick={goToCheckout} className="w-full mt-4 p-4 rounded-lg border-none bg-gold text-navy-deep font-display text-[17px] sm:text-lg font-extrabold tracking-[1px] uppercase transition-all duration-200 hover:bg-gold-light hover:-translate-y-px hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] active:scale-[0.98] cursor-pointer" data-id="element-19">
          ▶ Comprar {PLAN_NAME[selectedPlan]} · {PLAN_PRICE[selectedPlan]}
        </button>
        <div className="flex items-center justify-center gap-4 mt-3 flex-wrap" data-id="element-20">
          <span className={`text-[11px] flex items-center gap-1 ${dark ? 'text-white/40' : 'text-text-light'}`} data-id="element-21">
            🔒 Pago seguro
          </span>
          <span className={`text-[11px] flex items-center gap-1 ${dark ? 'text-white/40' : 'text-text-light'}`} data-id="element-22">
            ⚡ Acceso en 5 min
          </span>
          <span className={`text-[11px] flex items-center gap-1 ${dark ? 'text-white/40' : 'text-text-light'}`} data-id="element-23">
            ✅ Garantía 7 días
          </span>
        </div>
      </div>;
  };
  return <div className="w-full relative" data-id="element-24">
      {/* TOPBAR */}
      <div className="bg-navy-deep py-2.5 border-b border-gold/20" data-id="element-25"></div>

      {/* HERO */}
      <section id="hero-section" className="bg-navy-deep pt-12 pb-14 sm:pt-20 sm:pb-[72px] relative overflow-hidden" data-id="element-26">
        <div className="absolute inset-0 hero-bg" data-id="element-27"></div>
        <div className="absolute inset-0 bg-blueprint bg-[length:48px_48px] pointer-events-none" data-id="element-28"></div>

        <div className="max-w-[1100px] mx-auto px-6 relative z-10 text-center" data-id="element-29">
          <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/25 text-gold font-display text-xs font-bold tracking-[3px] uppercase py-2 px-5 rounded mb-8 before:content-['▸'] before:text-[10px]" data-id="element-30">
            El sistema técnico N.º 1 para estudiantes de Arquitectura en
            Argentina
          </div>

          <h1 className="font-display font-extrabold tracking-[-1px] text-white mb-5 uppercase text-[40px]" data-id="element-31">
            Dejá de improvisar
            <br data-id="element-32" />
            en la <span className="text-gold" data-id="element-33">parte técnica</span>
            <br data-id="element-34" />
            de la carrera.
          </h1>

          <div className="mb-6 sm:mb-8 max-w-[720px] mx-auto" data-id="element-35">
            <img src="https://cdn.magicpatterns.com/uploads/aPnwwf1mg3TKvkBhqBDw1i/grok-image-75ef9cae-c16c-4ab0-91f7-8acfe05e3861.jpg" alt="Kit ARQ System™ — caja completa con los 8 tomos, bonos y herramientas sobre el escritorio de trabajo" className="w-full h-auto rounded-xl border border-gold/20 shadow-[0_20px_60px_rgba(0,0,0,0.45)]" data-id="element-36" />
          </div>

          <div className="flex items-center justify-center gap-5 mb-8 sm:gap-8 sm:mb-12 flex-wrap" data-id="element-37">
            <div className="flex flex-col items-center gap-0.5" data-id="element-38">
              <span className="font-display text-[28px] font-extrabold text-white leading-none" data-id="element-39">
                +400
              </span>
              <span className="text-[11px] font-medium text-gray-400 tracking-[1px] uppercase" data-id="element-40">
                Estudiantes
              </span>
            </div>
            <div className="w-px h-9 bg-white/10" data-id="element-41"></div>
            <div className="flex flex-col items-center gap-0.5" data-id="element-42">
              <span className="font-display text-[28px] font-extrabold text-white leading-none" data-id="element-43">
                4.9★
              </span>
              <span className="text-[11px] font-medium text-gray-400 tracking-[1px] uppercase" data-id="element-44">
                Valoración
              </span>
            </div>
            <div className="w-px h-9 bg-white/10" data-id="element-45"></div>
            <div className="flex flex-col items-center gap-0.5" data-id="element-46">
              <span className="font-display text-[28px] font-extrabold text-white leading-none" data-id="element-47">
                8
              </span>
              <span className="text-[11px] font-medium text-gray-400 tracking-[1px] uppercase" data-id="element-48">
                Tomos
              </span>
            </div>
            <div className="w-px h-9 bg-white/10" data-id="element-49"></div>
            <div className="flex flex-col items-center gap-0.5" data-id="element-50">
              <span className="font-display text-[28px] font-extrabold text-white leading-none" data-id="element-51">
                440+
              </span>
              <span className="text-[11px] font-medium text-gray-400 tracking-[1px] uppercase" data-id="element-52">
                Páginas
              </span>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-4" data-id="element-53">
            <button onClick={scrollToPricing} className="btn-primary" data-id="element-54">
              ▶ Quiero el Kit ARQ System™ ahora
            </button>
            <div className="flex items-center gap-4 flex-wrap justify-center mt-2" data-id="element-55">
              <span className="flex items-center gap-1.5 text-xs text-white/50" data-id="element-56">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 text-gold shrink-0" data-id="element-57">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-58" />
                </svg>
                Pago seguro
              </span>
              <span className="flex items-center gap-1.5 text-xs text-white/50" data-id="element-59">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 text-gold shrink-0" data-id="element-60">
                  <circle cx="12" cy="12" r="10" data-id="element-61" />
                  <path d="M12 6v6l4 2" data-id="element-62" />
                </svg>
                Acceso en 5 minutos
              </span>
              <span className="flex items-center gap-1.5 text-xs text-white/50" data-id="element-63">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 text-gold shrink-0" data-id="element-64">
                  <path d="M9 12l2 2 4-4" data-id="element-65" />
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" data-id="element-66" />
                </svg>
                Garantía 7 días
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT VISUAL */}
      <div className="bg-navy-deep pb-16 relative z-10" data-id="element-67"></div>

      {/* PAIN SECTION */}
      <section className="bg-off-white py-12 sm:py-20 relative before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px] before:bg-gradient-to-r before:from-transparent before:via-gold before:to-transparent" data-id="element-68">
        <div className="max-w-[1100px] mx-auto px-6" data-id="element-69">
          <span className="section-label" data-id="element-70">¿Te pasa esto?</span>
          <h2 className="font-display text-[clamp(30px,5vw,48px)] font-extrabold text-navy text-center uppercase leading-[1.1] mb-3" data-id="element-71">
            Si estudiás Arquitectura
            <br data-id="element-72" />
            en Argentina, esto te suena
          </h2>
          <p className="text-center text-text-light text-base mb-8 sm:mb-[52px]" data-id="element-73">
            Elegí cuántos de estos puntos te identifican.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-px bg-gray-200 rounded-[10px] overflow-hidden" data-id="element-74">
            {[{
            icon: '📐',
            title: 'Estructuras te aplaza cuatrimestre tras cuatrimestre',
            desc: 'Sabés la teoría pero al momento de aplicarla al proyecto no cierra. El razonamiento se corta.'
          }, {
            icon: '🏗️',
            title: 'Construcciones sin criterio real',
            desc: 'Dibujás detalles que nunca entendiste. El docente los señala y vos no sabés por qué están mal.'
          }, {
            icon: '⚡',
            title: 'Instalaciones al final y siempre incompletas',
            desc: 'Los caños van donde entran, no donde deberían. El plano de instalaciones lo hacés el último día.'
          }, {
            icon: '📏',
            title: 'No sabés cómo predimensionar',
            desc: 'Te piden la sección de la columna y te quedás en blanco. No hay fórmula que te cierre.'
          }, {
            icon: '🗂️',
            title: 'La memoria técnica siempre queda floja',
            desc: 'No sabés qué tiene que tener ni en qué orden. La escribís de último y se nota.'
          }, {
            icon: '🎯',
            title: 'El jury te agarra sin argumentos técnicos',
            desc: 'El jurado pregunta por qué elegiste ese sistema estructural y la respuesta no convence.'
          }, {
            icon: '📚',
            title: 'Apuntes sueltos que no explican nada',
            desc: 'Buscás ejercicios resueltos en grupos de WhatsApp y los que encontrás no tienen el razonamiento completo.'
          }, {
            icon: '🔄',
            title: 'Todo junto en el nivel 4 y nada cierra',
            desc: 'Estructuras, Construcciones e Instalaciones al mismo tiempo y el proyecto parece imposible de integrar.'
          }, {
            icon: '💼',
            title: 'La carrera no te enseña a cobrar',
            desc: 'No sabés cuánto vale tu trabajo, cómo presupuestar ni qué tiene que decir un contrato.'
          }].map((pain, i) => {
            const selected = selectedPains.includes(i);
            return <button key={i} type="button" onClick={() => togglePain(i)} aria-pressed={selected} className={`relative text-left p-5 sm:p-7 sm:px-6 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold ${selected ? 'bg-gold/[0.07] ring-2 ring-inset ring-gold z-10' : 'bg-white hover:bg-off-white'}`} data-id="element-75">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-base shrink-0 transition-colors duration-200 ${selected ? 'bg-gold/20' : 'bg-navy/5'}`} data-id="element-76">
                    {pain.icon}
                  </div>
                  <h4 className="font-display text-[15px] font-bold text-navy uppercase tracking-[0.5px] pr-6" data-id="element-77">
                    {pain.title}
                  </h4>
                  <p className="text-[13px] text-text-light leading-[1.55]" data-id="element-78">
                    {pain.desc}
                  </p>
                  <span className={`absolute top-4 right-4 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-200 ${selected ? 'bg-gold scale-100 opacity-100' : 'bg-gray-200 scale-90 opacity-40'}`} aria-hidden="true" data-id="element-79">
                    <CheckIcon className={`w-3 h-3 ${selected ? 'text-navy-deep' : 'text-gray-400'}`} strokeWidth={3} data-id="element-80" />
                  </span>
                </button>;
          })}
          </div>

          {selectedPains.length > 0 && <div className="text-center mt-6" data-id="element-81">
              <span className="inline-flex items-center gap-2 bg-navy text-white font-display text-sm font-bold tracking-[1px] uppercase py-2 px-5 rounded-full" data-id="element-82">
                <CheckIcon className="w-4 h-4 text-gold" strokeWidth={3} data-id="element-83" />
                {selectedPains.length} de 9 te identifican
              </span>
            </div>}

          <div className="text-center mt-10 sm:mt-[52px] font-display text-[clamp(22px,4vw,36px)] font-extrabold text-navy uppercase leading-[1.2]" data-id="element-84">
            ¿Cuántos cuatrimestres más vas a improvisar
            <br data-id="element-85" />
            la <em className="text-blue not-italic" data-id="element-86">parte técnica</em> de tu
            carrera?
          </div>
        </div>
      </section>

      {/* FOR WHOM */}
      <section className="bg-white py-12 sm:py-20" data-id="element-87">
        <div className="max-w-[1100px] mx-auto px-6" data-id="element-88">
          <span className="section-label" data-id="element-89">¿Para quién es?</span>
          <h2 className="font-display text-[clamp(28px,5vw,44px)] font-extrabold text-navy uppercase text-center mb-3" data-id="element-90">
            Diseñado específicamente para vos
          </h2>
          <p className="text-center text-text-light mb-8 sm:mb-[52px] text-[15px]" data-id="element-91">
            Si alguna de estas situaciones te describe, el Kit ARQ System™ es
            para vos.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-92">
            {[{
            num: '01',
            title: 'Estudiantes de cualquier año',
            desc: 'Del primer año al último. Las materias técnicas arrancan en segundo y no paran. Este kit te acompaña en todo el recorrido sin importar en qué etapa estés.'
          }, {
            num: '02',
            title: 'De cualquier facultad del país',
            desc: 'La UBA, UNC, UNLP, UTN, privadas. El contenido no menciona ninguna facultad porque el dolor técnico es el mismo en todas. Aplica a tu cursada directamente.'
          }, {
            num: '03',
            title: 'Que cursás varias materias técnicas juntas',
            desc: 'El nivel donde Estructuras, Construcciones e Instalaciones se cruzan en el mismo cuatrimestre. El tomo integrador y el cronograma existen exactamente para ese momento.'
          }, {
            num: '04',
            title: 'Que querés empezar a ejercer antes de recibirte',
            desc: 'El Tomo 08 del Kit ARQ Total™ te da presupuesto, contrato, honorarios y primeros encargos. El kit que convierte al estudiante en arquitecto.'
          }].map((item, i) => <div key={i} className="border-[1.5px] border-gray-200 rounded-[10px] p-5 sm:p-6 flex gap-4 transition-all duration-200 hover:border-blue hover:shadow-[0_4px_20px_rgba(46,109,164,0.1)]" data-id="element-93">
                <div className="font-display text-3xl font-extrabold text-gray-200 leading-none shrink-0 w-10" data-id="element-94">
                  {item.num}
                </div>
                <div data-id="element-95">
                  <h4 className="font-display text-base font-bold text-navy uppercase mb-1.5" data-id="element-96">
                    {item.title}
                  </h4>
                  <p className="text-[13px] text-text-light leading-[1.55]" data-id="element-97">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* TOMOS SECTION */}
      <section className="bg-navy-deep py-12 sm:py-20 relative overflow-hidden" data-id="element-98">
        <div className="absolute inset-0 bg-blueprint-dark bg-[length:32px_32px]" data-id="element-99"></div>
        <div className="max-w-[1100px] mx-auto px-6 relative z-10" data-id="element-100">
          <span className="section-label" data-id="element-101">El contenido completo</span>
          <h2 className="font-display text-[clamp(28px,5vw,44px)] font-extrabold text-white uppercase text-center mb-2" data-id="element-102">
            Los 8 Tomos del Kit ARQ System™
          </h2>
          <p className="text-center text-white/50 mb-8 sm:mb-[52px] text-sm" data-id="element-103">
            50 a 60 páginas por tomo · Ejercicios resueltos con razonamiento
            visible en cada paso · Herramientas aplicables de inmediato
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3" data-id="element-104">
            {[{
            num: '01',
            tag: 'Estructuras',
            title: 'Cómo funciona una estructura',
            pages: '50 págs',
            desc: 'Del concepto al cálculo básico. Cargas, vigas, losas y columnas explicados con lógica visual. Ejercicios resueltos con razonamiento completo visible en cada paso.',
            img: IMG('photo-1503387762-592deb58ef4e')
          }, {
            num: '02',
            tag: 'Construcciones',
            title: 'Cómo se construye lo que diseñás',
            pages: '50 págs',
            desc: 'Sistemas constructivos, materialidad y detalles explicados desde el proyecto. Los errores de parcial más comunes y cómo no cometerlos.',
            img: IMG('photo-1541888946425-d81bb19240f5')
          }, {
            num: '03',
            tag: 'Instalaciones',
            title: 'Sanitarias, eléctricas y gas',
            pages: '50 págs',
            desc: 'Esquemas claros, normativa esencial y dimensionado básico. Los ejercicios tipo de todos los finales del país, resueltos paso a paso.',
            img: IMG('photo-1581092160607-ee22621dd758')
          }, {
            num: '04',
            tag: 'Morfología',
            title: 'La lógica de la forma',
            pages: '50 págs',
            desc: 'Composición, espacio y razonamiento proyectual sin filosofía vacía. Cómo traducir conceptos morfológicos en decisiones concretas de proyecto.',
            img: IMG('photo-1487958449943-2429e8be8625')
          }, {
            num: '05',
            tag: 'Integrador',
            title: 'Cuando todo se junta',
            pages: '60 págs',
            desc: 'Cómo integrar estructura, construcción e instalaciones en un proyecto sin que ninguna disciplina se contradiga. El año donde más gente traba, resuelto con método.',
            img: IMG('photo-1503387837-b154d5074bd2')
          }, {
            num: '06',
            tag: 'Documentación',
            title: 'Del plano a la obra',
            pages: '60 págs',
            desc: 'Cómo producir la documentación técnica completa: plantas, cortes, detalles y memoria. Lo que el jurado espera ver y nadie te enseña a producir en la cursada.',
            img: IMG('photo-1493397212122-2b85dda8106b')
          }, {
            num: '07',
            tag: 'Exclusivo ARQ Total™',
            title: 'Hormigón armado para arquitectos',
            pages: '60 págs',
            desc: 'Losas, vigas y columnas sin ingeniero al lado. El tomo que convierte la materia que más aplaza en la que más dominás.',
            img: IMG('photo-1590986701849-5a1cd6a5c9e2'),
            exclusive: true
          }, {
            num: '08',
            tag: 'Exclusivo ARQ Total™',
            title: 'Presupuesto y ejercicio profesional',
            pages: '60 págs',
            desc: 'Cómputo, presupuesto, contrato y primeros encargos. Lo que la carrera no te enseña pero el mercado te exige desde el primer cliente.',
            img: IMG('photo-1554224155-6726b3ff858f'),
            exclusive: true
          }].map((tomo, i) => {
            const open = openTomo === i;
            return <div key={i} className={`group bg-white/5 border rounded-[10px] overflow-hidden transition-all duration-200 ${open ? 'border-gold/40 bg-white/[0.09]' : 'border-white/5 hover:bg-white/[0.09] hover:border-gold/30'} ${tomo.exclusive ? 'border-gold/25 bg-gold/5' : ''}`} data-id="element-105">
                  <button type="button" onClick={() => setOpenTomo(open ? null : i)} aria-expanded={open} className="w-full flex items-center gap-3 sm:gap-4 text-left p-2.5 sm:p-3 cursor-pointer active:scale-[0.99] transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-[10px]" data-id="element-106">
                    <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden shrink-0" data-id="element-107">
                      <img src={tomo.img} alt={tomo.title} loading="lazy" decoding="async" width={160} height={160} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-108" />
                      <span className={`absolute inset-0 ${tomo.exclusive ? 'bg-gold/25' : 'bg-navy-deep/45'}`} data-id="element-109"></span>
                      <span className="absolute bottom-1 left-1.5 font-display text-lg font-extrabold text-white leading-none drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]" data-id="element-110">
                        {tomo.num}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-111">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap" data-id="element-112">
                        <span className={`text-[10px] font-bold tracking-[1.5px] uppercase ${tomo.exclusive ? 'text-gold-light' : 'text-gold'}`} data-id="element-113">
                          {tomo.tag}
                        </span>
                        <span className="text-[10px] font-semibold text-white/30" data-id="element-114">
                          · {tomo.pages}
                        </span>
                      </div>
                      <h3 className="font-display text-[15px] sm:text-base font-bold text-white uppercase leading-[1.15] truncate" data-id="element-115">
                        {tomo.title}
                      </h3>
                    </div>
                    <ChevronDownIcon className={`w-4 h-4 text-white/40 shrink-0 transition-transform duration-200 ${open ? 'rotate-180 text-gold' : ''}`} data-id="element-116" />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-40' : 'max-h-0'}`} data-id="element-117">
                    <p className="text-[13px] text-white/55 leading-[1.5] px-3 pb-3.5 sm:px-4 sm:pl-[76px]" data-id="element-118">
                      {tomo.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* BONOS */}
      <section className="bg-white py-12 sm:py-20" data-id="element-119">
        <div className="max-w-[1100px] mx-auto px-6" data-id="element-120">
          <span className="section-label" data-id="element-121">Incluido sin costo extra</span>
          <h2 className="font-display text-[clamp(28px,5vw,44px)] font-extrabold text-navy uppercase text-center mb-2" data-id="element-122">
            6 Bonos Digitales
          </h2>
          <p className="text-center text-text-light mb-8 sm:mb-[52px] text-sm" data-id="element-123">
            Herramientas listas para usar desde el primer trabajo práctico ·
            Incluidas en ambas ofertas
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 sm:gap-3" data-id="element-124">
            {[{
            num: '01',
            title: 'Planilla de Predimensionado',
            val: '$5.990',
            desc: 'Excel con fórmulas listas para estimar secciones de vigas, losas y columnas sin hacer el cálculo desde cero.',
            img: IMG('photo-1460925895917-afdab827c52f')
          }, {
            num: '02',
            title: 'Esquemas de Instalaciones',
            val: '$4.990',
            desc: '45 esquemas editables de sanitarias, eléctricas y gas listos para insertar en cualquier proyecto de la cursada.',
            img: IMG('photo-1581093458791-9d42e3c7e117')
          }, {
            num: '03',
            title: 'Checklist de Entrega Técnica',
            val: '$3.490',
            desc: 'Los 35 puntos que revisás antes de imprimir para no perder nota por errores que ya cometiste antes.',
            img: IMG('photo-1454165804606-c3d57bc86b40')
          }, {
            num: '04',
            title: 'Glosario Técnico Visual',
            val: '$3.490',
            desc: '70 términos con definición, imagen de referencia y uso correcto en parciales y finales. Para cualquier facultad del país.',
            img: IMG('photo-1544716278-ca5e3f4abd8c')
          }, {
            num: '05',
            title: 'Cronograma de Cursada',
            val: '$2.990',
            desc: 'Planilla semanal para cursar Estructuras + Construcciones + Instalaciones en paralelo sin que ninguna te explote encima.',
            img: IMG('photo-1506784983877-45594efa4cbe')
          }, {
            num: '06',
            title: 'Plantilla de Memoria Técnica',
            val: '$2.990',
            desc: 'Estructura lista para completar con los bloques que todo docente espera leer. Funciona para cualquier proyecto y cualquier facultad.',
            img: IMG('photo-1517842645767-c639042777db')
          }].map((bono, i) => {
            const open = openBono === i;
            return <div key={i} className={`group border-[1.5px] rounded-[10px] relative overflow-hidden transition-all duration-200 ${open ? 'border-gold shadow-[0_4px_20px_rgba(201,168,76,0.12)]' : 'border-gray-200 hover:border-gold hover:shadow-[0_4px_20px_rgba(201,168,76,0.12)]'}`} data-id="element-125">
                  <button type="button" onClick={() => setOpenBono(open ? null : i)} aria-expanded={open} className="w-full flex items-center gap-3 text-left p-2.5 cursor-pointer active:scale-[0.99] transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-[10px]" data-id="element-126">
                    <div className="relative w-14 h-14 rounded-lg overflow-hidden shrink-0" data-id="element-127">
                      <img src={bono.img} alt={bono.title} loading="lazy" decoding="async" width={160} height={160} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-128" />
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-129">
                      <span className="font-display text-[10px] font-bold tracking-[1.5px] text-gold uppercase block mb-0.5" data-id="element-130">
                        Bono {bono.num}
                      </span>
                      <h4 className="font-display text-[13px] font-bold text-navy uppercase leading-[1.15] mb-1" data-id="element-131">
                        {bono.title}
                      </h4>
                      <span className="inline-block font-display text-[11px] font-bold text-success" data-id="element-132">
                        <del className="text-gray-400 mr-1 font-semibold" data-id="element-133">
                          {bono.val}
                        </del>
                        GRATIS
                      </span>
                    </div>
                    <ChevronDownIcon className={`w-4 h-4 text-gray-300 shrink-0 transition-transform duration-200 ${open ? 'rotate-180 text-gold' : ''}`} data-id="element-134" />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-40' : 'max-h-0'}`} data-id="element-135">
                    <p className="text-xs text-text-light leading-[1.5] px-3 pb-3.5" data-id="element-136">
                      {bono.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>

          <div className="text-center mt-8 p-6 sm:mt-10 sm:p-7 bg-off-white rounded-[10px] border border-gray-200" data-id="element-137">
            <div className="text-[13px] text-text-light mb-1" data-id="element-138">
              Valor total de los 6 bonos
            </div>
            <div className="font-display text-[32px] font-extrabold text-navy" data-id="element-139">
              <del className="text-gray-400 mr-2 text-2xl" data-id="element-140">$23.940</del>
              <span className="text-success" data-id="element-141">GRATIS</span>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-off-white py-12 sm:py-20" data-id="element-142">
        <div className="max-w-[960px] mx-auto px-6" data-id="element-143">
          <span className="section-label" data-id="element-144">¿Cuánto te cuesta no tenerlo?</span>
          <h2 className="font-display text-[clamp(28px,4vw,40px)] font-extrabold text-navy uppercase text-center mb-2" data-id="element-145">
            Sin el sistema vs. con el sistema
          </h2>
          <p className="text-center text-text-light text-sm mb-8 sm:mb-12 max-w-[520px] mx-auto" data-id="element-146">
            La misma cursada, dos caminos muy distintos. Mirá el antes y el
            después de tener el Kit ARQ System™.
          </p>

          {(() => {
          const rows = [['Horas buscando ejercicios resueltos en grupos de WhatsApp', 'El ejercicio resuelto con razonamiento completo en el tomo correspondiente'], ['Sin método de predimensionado → columnas y vigas al azar', 'Planilla de predimensionado lista: ingresás la luz y obtenés la sección'], ['Instalaciones dibujadas a último momento, sin criterio', '45 esquemas de instalaciones listos para adaptar a tu proyecto'], ['Jury sin argumentos técnicos para defender las decisiones', 'Vocabulario técnico, guión de defensa y frases modelo incluidos'], ['Cursada técnica en caos: estructuras, construcciones e instalaciones juntas sin método', 'Cronograma semanal con sistema de prioridades para llegar a todas'], ['Memoria técnica de última hora que el docente señala en la corrección', 'Plantilla con estructura completa lista para completar con tus datos'], ['Sin saber cuánto cobrar ni cómo armar un contrato para el primer encargo', 'Honorarios, contrato modelo y kit de primer encargo incluidos en el Tomo 08']];
          return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 items-stretch" data-id="element-147">
                {/* PANEL SIN */}
                <div className="rounded-2xl border-[1.5px] border-gray-200 bg-white p-5 sm:p-6" data-id="element-148">
                  <div className="flex items-center gap-2.5 mb-5" data-id="element-149">
                    <span className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0" data-id="element-150">
                      <XIcon className="w-4 h-4 text-gray-400" strokeWidth={2.5} data-id="element-151" />
                    </span>
                    <h3 className="font-display text-[15px] font-bold tracking-[1px] uppercase text-gray-400" data-id="element-152">
                      Sin el sistema
                    </h3>
                  </div>
                  <ul className="flex flex-col gap-3.5" data-id="element-153">
                    {rows.map((row, i) => <li key={i} className="flex items-start gap-3" data-id="element-154">
                        <XIcon className="w-4 h-4 text-gray-300 shrink-0 mt-0.5" data-id="element-155" />
                        <span className="text-[13px] leading-[1.5] text-text-light" data-id="element-156">
                          {row[0]}
                        </span>
                      </li>)}
                  </ul>
                </div>

                {/* PANEL CON — destacado */}
                <div className="relative rounded-2xl border-2 border-gold bg-navy p-5 sm:p-6 shadow-[0_16px_50px_rgba(26,58,92,0.2)] overflow-hidden" data-id="element-157">
                  <div className="absolute inset-0 bg-blueprint-dark bg-[length:28px_28px] opacity-40 pointer-events-none" data-id="element-158"></div>
                  <span className="absolute -top-px right-5 bg-gold text-navy-deep font-display text-[10px] font-extrabold tracking-[1.5px] uppercase py-1 px-3 rounded-b-md" data-id="element-159">
                    ★ La solución
                  </span>
                  <div className="relative flex items-center gap-2.5 mb-5" data-id="element-160">
                    <span className="w-9 h-9 rounded-full bg-gold flex items-center justify-center shrink-0" data-id="element-161">
                      <CheckIcon className="w-4 h-4 text-navy-deep" strokeWidth={3} data-id="element-162" />
                    </span>
                    <h3 className="font-display text-[15px] font-bold tracking-[1px] uppercase text-gold" data-id="element-163">
                      Con el sistema
                    </h3>
                  </div>
                  <ul className="relative flex flex-col gap-3.5" data-id="element-164">
                    {rows.map((row, i) => <li key={i} className="flex items-start gap-3" data-id="element-165">
                        <CheckIcon className="w-4 h-4 text-success shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-166" />
                        <span className="text-[13px] leading-[1.5] text-white/90 font-medium" data-id="element-167">
                          {row[1]}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>;
        })()}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-navy-deep py-12 sm:py-20" data-id="element-168">
        <div className="max-w-[1100px] mx-auto px-6" data-id="element-169">
          <span className="section-label" data-id="element-170">
            Lo que dicen los que ya lo tienen
          </span>
          <h2 className="font-display text-[clamp(28px,5vw,44px)] font-extrabold text-white uppercase text-center mb-2" data-id="element-171">
            Resultados reales
          </h2>
          <p className="text-center text-white/40 mb-8 sm:mb-[52px] text-sm" data-id="element-172">
            Estudiantes de arquitectura de toda Argentina
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-173">
            {[{
            text: '"Estructuras siempre fue mi pesadilla. Con el Tomo 01 entendí la lógica que nunca me explicaron. Llegué al jury con la trama estructural justificada y el docente no tuvo nada que señalar."',
            initials: 'ML',
            name: 'Martina L.',
            meta: 'Córdoba — Arquitectura, UNC'
          }, {
            text: '"El Tomo 03 me salvó el cuatrimestre. Nunca había entendido cómo coordinar instalaciones con estructura. Con los esquemas ya lo armé en una tarde. La corrección fue la mejor que tuve en el año."',
            initials: 'FR',
            name: 'Federico R.',
            meta: 'Buenos Aires — Arquitectura, FADU'
          }, {
            text: '"Venía arrastrando Construcciones hace dos cuatrimestres. El Tomo 02 me explicó los detalles de una forma que nunca me lo habían dicho en la facultad. Aprobé con 8."',
            initials: 'VP',
            name: 'Valentina P.',
            meta: 'La Plata — Arquitectura, UNLP'
          }, {
            text: '"El Tomo 05 es el que más usé. Cuando llegué al nivel donde cursaba todo junto no sabía por dónde empezar. El sistema de integración me ordenó el proyecto y llegué a la entrega con todo cerrado."',
            initials: 'SG',
            name: 'Santiago G.',
            meta: 'Rosario — Arquitectura, UNR'
          }, {
            text: '"La Planilla de Predimensionado me cambió la forma de arrancar un proyecto. Antes tardaba horas. Ahora en 20 minutos tengo las secciones orientativas de todo y puedo avanzar."',
            initials: 'LN',
            name: 'Luciana N.',
            meta: 'Mendoza — Arquitectura, UNCuyo'
          }, {
            text: '"Compré el Kit ARQ Total para tener el Tomo de Hormigón Armado. Lo que más me sorprendió es que por primera vez entendí por qué la armadura va donde va. No solo cómo, sino por qué."',
            initials: 'TM',
            name: 'Tomás M.',
            meta: 'CABA — Arquitectura, UADE'
          }].map((test, i) => <div key={i} className="bg-white/5 border border-white/5 rounded-[10px] p-6 flex flex-col gap-4 transition-colors duration-200 hover:border-gold/25" data-id="element-174">
                <div className="text-gold text-[13px] tracking-[2px]" data-id="element-175">
                  ★★★★★
                </div>
                <blockquote className="text-sm text-white/75 leading-[1.65] italic flex-1" data-id="element-176">
                  {test.text}
                </blockquote>
                <div className="flex items-center gap-3" data-id="element-177">
                  <div className="w-9 h-9 rounded-full tome-icon-gradient border-[1.5px] border-gold/30 flex items-center justify-center font-display text-sm font-extrabold text-white shrink-0" data-id="element-178">
                    {test.initials}
                  </div>
                  <div data-id="element-179">
                    <div className="font-display text-[13px] font-bold text-white uppercase" data-id="element-180">
                      {test.name}
                    </div>
                    <div className="text-[11px] text-white/40" data-id="element-181">{test.meta}</div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* TRUST / NÚMEROS */}
      <section className="bg-off-white py-12 sm:py-20 relative before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px] before:bg-gradient-to-r before:from-transparent before:via-gold before:to-transparent" data-id="element-182">
        <div className="max-w-[1000px] mx-auto px-6" data-id="element-183">
          <div className="flex justify-center mb-5" data-id="element-184">
            <span className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 text-gold font-display text-[11px] sm:text-xs font-bold tracking-[2px] uppercase py-2 px-5 rounded-full text-center" data-id="element-185">
              <StarIcon className="w-3.5 h-3.5 fill-gold" data-id="element-186" />
              El sistema técnico N.º 1 para estudiantes de Arquitectura en
              Argentina
            </span>
          </div>

          {/* LOGO */}
          <div className="flex justify-center mb-8 sm:mb-10" data-id="element-187">
            <div className="bg-navy-deep rounded-2xl border border-gold/20 p-5 sm:p-6 shadow-[0_10px_40px_rgba(15,37,64,0.18)]" data-id="element-188">
              <img src="https://cdn.magicpatterns.com/uploads/oG1tQnuUXE42BZYHQMknP8/logo_(2).png" alt="InfoDigital Core" loading="lazy" decoding="async" width={220} height={220} className="w-32 h-32 sm:w-40 sm:h-40 object-contain" data-id="element-189" />
            </div>
          </div>

          {/* NÚMEROS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10" data-id="element-190">
            {[{
            icon: UsersIcon,
            value: '+400',
            label: 'Estudiantes'
          }, {
            icon: StarIcon,
            value: '4.9★',
            label: 'Valoración'
          }, {
            icon: LayersIcon,
            value: '8',
            label: 'Tomos'
          }, {
            icon: FileTextIcon,
            value: '440+',
            label: 'Páginas'
          }].map((stat, i) => <div key={i} className="flex flex-col items-center text-center bg-white border-[1.5px] border-gray-200 rounded-2xl py-6 px-3 transition-all duration-200 hover:border-gold hover:shadow-[0_8px_30px_rgba(201,168,76,0.12)]" data-id="element-191">
                <span className="w-11 h-11 rounded-full bg-gold/12 flex items-center justify-center mb-3" data-id="element-192">
                  <stat.icon className="w-5 h-5 text-gold" strokeWidth={2.2} data-id="element-193" />
                </span>
                <span className="font-display text-[34px] sm:text-[40px] font-extrabold text-navy leading-none mb-1" data-id="element-194">
                  {stat.value}
                </span>
                <span className="text-[11px] font-semibold text-text-light tracking-[1.5px] uppercase" data-id="element-195">
                  {stat.label}
                </span>
              </div>)}
          </div>

          {/* SELLOS DE CONFIANZA */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-[760px] mx-auto" data-id="element-196">
            {[{
            icon: ShieldCheckIcon,
            title: 'Pago seguro',
            sub: 'Procesado con Mercado Pago'
          }, {
            icon: ZapIcon,
            title: 'Acceso en 5 minutos',
            sub: 'Descarga inmediata tras el pago'
          }, {
            icon: CheckIcon,
            title: 'Garantía 7 días',
            sub: 'Te devolvemos el 100%'
          }].map((seal, i) => <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl py-3.5 px-4" data-id="element-197">
                <span className="w-10 h-10 rounded-full bg-success/12 flex items-center justify-center shrink-0" data-id="element-198">
                  <seal.icon className="w-5 h-5 text-success" strokeWidth={2.4} data-id="element-199" />
                </span>
                <div className="min-w-0" data-id="element-200">
                  <div className="font-display text-[13px] font-bold text-navy uppercase leading-tight" data-id="element-201">
                    {seal.title}
                  </div>
                  <div className="text-[11px] text-text-light leading-tight mt-0.5" data-id="element-202">
                    {seal.sub}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* TIMER */}

      {/* PRICING */}
      <section id="pricing" className="bg-white py-12 sm:py-20" data-id="element-203">
        <div className="max-w-[1100px] mx-auto px-6" data-id="element-204">
          <span className="section-label" data-id="element-205">Elegí tu kit</span>
          <h2 className="font-display text-[clamp(28px,5vw,44px)] font-extrabold text-navy uppercase text-center mb-2" data-id="element-206">
            Las dos ofertas del Kit ARQ System™
          </h2>
          <p className="text-center text-text-light mb-8 sm:mb-[52px] text-sm" data-id="element-207">
            Ambas incluyen los 6 bonos digitales completos · Acceso inmediato ·
            PDF descargable
          </p>

          <PlanSelector variant="light" data-id="element-208" />
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="bg-off-white py-10 sm:py-16" data-id="element-209">
        <div className="max-w-[1100px] mx-auto px-6" data-id="element-210"></div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-12 sm:py-20" data-id="element-211">
        <div className="max-w-[780px] mx-auto px-6" data-id="element-212">
          <h2 className="font-display text-[clamp(28px,5vw,40px)] font-extrabold text-navy uppercase text-center mb-8 sm:mb-12" data-id="element-213">
            Preguntas frecuentes
          </h2>

          <div className="flex flex-col max-w-[700px] mx-auto" data-id="element-214">
            {[{
            q: '¿Cómo recibo el kit después de comprar?',
            a: 'Inmediatamente después de confirmar el pago recibís un email con el link de descarga. El acceso es tuyo para siempre: lo descargás una vez y lo tenés en tu dispositivo sin necesidad de conexión.'
          }, {
            q: '¿Sirve para cualquier facultad de Argentina?',
            a: 'Sí. El kit no menciona ninguna facultad específica. El contenido habla por dolor y por área de conocimiento, no por nombre de materia o cátedra. Aplica a la UBA, UNC, UNLP, UTN, universidades privadas y cualquier otra institución donde se curse arquitectura en el país.'
          }, {
            q: '¿Sirve si recién empiezo la carrera o es solo para años avanzados?',
            a: 'Sirve para cualquier año. Las materias técnicas arrancan en segundo y se extienden hasta el final. Cada tomo es independiente: usás el que corresponde a la materia que estás cursando ahora y el resto te va a servir después.'
          }, {
            q: '¿Necesito algún programa especial para usar los bonos?',
            a: 'No. Los tomos son PDFs que abrís en cualquier dispositivo. La planilla de predimensionado es un Excel que funciona en cualquier versión de Excel o Google Sheets. Los esquemas son PDFs editables. No necesitás ningún software de pago.'
          }, {
            q: '¿Qué diferencia hay entre el Kit ARQ Core™ y el Kit ARQ Total™?',
            a: 'El Kit ARQ Core™ tiene los 6 tomos base que cubren estructuras, construcciones, instalaciones, morfología, integración y documentación. El Kit ARQ Total™ incluye todo lo anterior más el Tomo 07 de Hormigón Armado (la materia que más aplaza) y el Tomo 08 de Ejercicio Profesional (presupuesto, honorarios y primeros encargos). Si cursás hormigón o querés empezar a ejercer antes de recibirte, el Total es la elección correcta.'
          }, {
            q: '¿Qué diferencia tiene con apuntes gratis de grupos de WhatsApp?',
            a: 'Los apuntes de WhatsApp son ejercicios sueltos sin razonamiento, sin contexto y sin garantía de que estén bien. El Kit ARQ System™ es un sistema con ejercicios resueltos mostrando el razonamiento completo en cada paso, herramientas aplicables de inmediato (planillas, checklists, esquemas) y un orden lógico que te lleva desde el concepto hasta la entrega. No es un apunte. Es un método.'
          }, {
            q: '¿Tiene garantía real?',
            a: 'Sí. 7 días de garantía total. Si abrís los tomos, usás las herramientas en tu próximo trabajo práctico y sentís que no fue lo que esperabas, nos escribís por email y te devolvemos el 100% sin preguntas, sin formularios y sin drama. La tasa de devoluciones es menor al 1%.'
          }].map((faq, i) => <div key={i} className="border-b border-gray-100 overflow-hidden" data-id="element-215">
                <button onClick={() => toggleFaq(i)} className="w-full bg-transparent border-none cursor-pointer flex items-center justify-between gap-4 py-4 sm:py-5 text-left font-body text-[15px] font-semibold text-navy transition-colors duration-200 hover:text-blue active:scale-[0.98]" data-id="element-216">
                  {faq.q}
                  <svg className={`w-5 h-5 shrink-0 text-gray-400 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-217">
                    <path d="M6 9l6 6 6-6" data-id="element-218" />
                  </svg>
                </button>
                <div className={`text-sm text-text-light leading-[1.65] transition-all duration-300 ease-in-out overflow-hidden ${openFaq === i ? 'max-h-[300px] pb-5' : 'max-h-0'}`} data-id="element-219">
                  {faq.a}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-navy-deep py-12 sm:py-20 relative overflow-hidden" data-id="element-220">
        <div className="absolute inset-0 bg-blueprint-dark bg-[length:32px_32px]" data-id="element-221"></div>
        <div className="max-w-[1100px] mx-auto px-6 relative z-10 text-center" data-id="element-222">
          <span className="section-label" data-id="element-223">No dejes pasar esta oportunidad</span>
          <h2 className="font-display text-[clamp(36px,6vw,64px)] font-extrabold text-white uppercase leading-[0.95] mb-4" data-id="element-224">
            El sistema técnico
            <br data-id="element-225" />
            que la facultad
            <br data-id="element-226" />
            <span className="text-gold" data-id="element-227">nunca te dio.</span>
          </h2>
          <p className="text-base text-white/50 mb-8 sm:mb-10 max-w-[500px] mx-auto leading-[1.6]" data-id="element-228">
            Descargá. Aplicalo en tu próximo trabajo práctico.
            <br data-id="element-229" />
            Si no es lo que esperabas, te devolvemos el 100%.
          </p>

          <PlanSelector variant="dark" data-id="element-230" />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#080F17] py-10 border-t border-white/5" data-id="element-231">
        <div className="max-w-[1100px] mx-auto px-6" data-id="element-232">
          <div className="flex items-center justify-between flex-wrap gap-5" data-id="element-233">
            <div data-id="element-234">
              <div className="font-display text-base font-extrabold text-white uppercase tracking-[2px]" data-id="element-235">
                KIT ARQ SYSTEM<span className="text-gold" data-id="element-236">™</span>
              </div>
              <div className="text-xs text-white/25 mt-1" data-id="element-237">
                by INFODIGITAL CORE
              </div>
            </div>
            <div className="flex gap-6 flex-wrap" data-id="element-238">
              <a href="#" className="text-xs text-white/30 no-underline transition-colors duration-200 hover:text-white/70" data-id="element-239">
                soporteIDC1@outlook.com
              </a>
              <a href="#" className="text-xs text-white/30 no-underline transition-colors duration-200 hover:text-white/70" data-id="element-240">
                @infordigitalcore
              </a>
              <a href="#" className="text-xs text-white/30 no-underline transition-colors duration-200 hover:text-white/70" data-id="element-241">
                infodigitalcore.com
              </a>
            </div>
            <div className="text-xs text-white/20" data-id="element-242">
              © infodigitalcore.com™ 2026 — Todos los derechos reservados.
            </div>
          </div>
        </div>
      </footer>

      {/* STICKY CTA */}
      <div className={`fixed bottom-0 left-0 right-0 z-[100] bg-[#0A1424]/95 backdrop-blur-md border-t border-gold/20 py-3 px-6 transition-transform duration-300 ${isStickyVisible ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-243">
        <div className="max-w-[1100px] mx-auto flex items-center justify-between gap-4 flex-wrap" data-id="element-244">
          <div className="font-display text-base font-bold text-white" data-id="element-245">
            Kit ARQ System™ —{' '}
            <span className="text-gold" data-id="element-246">Oferta con tiempo limitado</span>
          </div>
          <button onClick={scrollToPricing} className="bg-gold text-navy-deep font-display text-sm font-extrabold tracking-[1px] uppercase py-3 px-7 rounded-md border-none cursor-pointer whitespace-nowrap transition-colors duration-200 hover:bg-gold-light" data-id="element-247">
            Ver ofertas ↓
          </button>
        </div>
      </div>

      {/* POPUP DE COMPRA VERIFICADA */}
      <div aria-live="polite" className="fixed bottom-20 left-3 right-3 sm:left-5 sm:right-auto z-[110] pointer-events-none flex sm:block justify-center" data-id="element-248">
        {purchase && <div key={purchase.id} role="status" className="pointer-events-auto w-full max-w-[330px] flex items-center gap-3 bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-[0_10px_40px_rgba(26,58,92,0.22)] p-3 pr-4 animate-purchase-in" data-id="element-249">
            <div className="w-10 h-10 rounded-full bg-success/12 flex items-center justify-center shrink-0" data-id="element-250">
              <ShieldCheckIcon className="w-5 h-5 text-success" strokeWidth={2.2} data-id="element-251" />
            </div>
            <div className="min-w-0 flex-1" data-id="element-252">
              <div className="flex items-center gap-1 mb-0.5" data-id="element-253">
                <span className="font-display text-[11px] font-extrabold tracking-[1px] text-success uppercase" data-id="element-254">
                  Compra verificada
                </span>
              </div>
              <p className="text-[13px] text-navy leading-snug" data-id="element-255">
                <span className="font-semibold" data-id="element-256">{purchase.name}</span>{' '}
                <span className="inline-flex items-center gap-0.5 text-text-light" data-id="element-257">
                  <MapPinIcon className="w-3 h-3" data-id="element-258" />
                  {purchase.city}
                </span>{' '}
                compró el{' '}
                <span className="font-semibold text-navy" data-id="element-259">
                  {purchase.offer}
                </span>
              </p>
              <span className="text-[11px] text-text-light" data-id="element-260">
                hace {purchase.minsAgo} min · Mercado Pago
              </span>
            </div>
            <button type="button" onClick={() => setPurchase(null)} aria-label="Cerrar aviso" className="text-gray-300 hover:text-gray-500 transition-colors shrink-0 -mt-1" data-id="element-261">
              <XIcon className="w-4 h-4" data-id="element-262" />
            </button>
          </div>}
      </div>
    </div>;
}